import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { FileText, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { z } from "zod";
import { DateRangeColumnFilter, MultiSelectColumnFilter } from "@/components/shared/column-filter";
import { type ColumnDefinition, ColumnManager } from "@/components/shared/column-manager";
import { ExportButton } from "@/components/shared/export-button";
import { NumberHoverCard } from "@/components/shared/number-hover-card";
import { type BreadcrumbTrailItem, PageBreadcrumbs } from "@/components/shared/page-breadcrumbs";
import { QuickFilterChip } from "@/components/shared/quick-filter-chip";
import { nextSortState, SortableHeader, type SortDir } from "@/components/shared/sortable-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SectionBar } from "@/components/ui/section-bar";
import { StatusBadge } from "@/components/ui/status-badge";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "@/components/ui/table";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useSessionState } from "@/hooks/use-session-state";
import { useTablePreferences } from "@/hooks/use-table-preferences";
import { apiGet } from "@/lib/api-client";
import { encodeFromAgency } from "@/lib/breadcrumb-from";
import type { ExportColumn } from "@/lib/export";
import { formatDate, toLocalIsoDate } from "@/lib/format";
import { policyKeys } from "@/lib/query";
import { policyStatusKey } from "@/lib/status-colors";
import { policyMrpSearchSchema } from "@/schemas/search";

const PolicyRowSchema = z.object({
  id: z.string(),
  policyNumber: z.string(),
  insuredName: z.string().nullable(),
  policyStatus: z.string().nullable(),
  effectiveDate: z.string().nullable(),
  expirationDate: z.string().nullable(),
  // openClaimCount powers the "Has open claims" quick-filter chip. The API
  // already includes this in every Policy MRP row from the legacy
  // PolicyCurrentAttribute query; we just hadn't been consuming it.
  openClaimCount: z.number().default(0),
});
const PolicyRowsSchema = z.array(PolicyRowSchema);
type PolicyRow = z.infer<typeof PolicyRowSchema>;

type SortKey = "policyNumber" | "insuredName" | "policyStatus" | "effectiveDate" | "expirationDate";

type SecondaryColumnKey = "insuredName" | "policyStatus" | "effectiveDate" | "expirationDate";

const POLICY_MRP_DEFAULT_ORDER: readonly SecondaryColumnKey[] = [
  "insuredName",
  "policyStatus",
  "effectiveDate",
  "expirationDate",
];

const POLICY_MRP_COLUMN_LABELS: Record<SecondaryColumnKey, string> = {
  insuredName: "Insured Name",
  policyStatus: "Status",
  effectiveDate: "Effective",
  expirationDate: "Expiration",
};

const POLICY_MRP_COLUMN_DEFS: readonly ColumnDefinition<SecondaryColumnKey>[] = (
  Object.keys(POLICY_MRP_COLUMN_LABELS) as SecondaryColumnKey[]
).map((key) => ({ key, label: POLICY_MRP_COLUMN_LABELS[key] }));

const PAGE_SIZE = 25;

function filterTitle(agencyCode: string | undefined, activePolicyBind: boolean): string {
  if (agencyCode && activePolicyBind) return `Active Policies on Agency ${agencyCode}`;
  if (agencyCode) return `All Policies on Agency ${agencyCode}`;
  return "All Policies";
}

export function PolicyDetailMrpPage() {
  // Re-parse with the same Zod schema the route's validateSearch uses. We
  // cannot use `useSearch({ from: "/policy/list" })` because that needs
  // TanStack Router typegen which this project doesn't have wired up. A
  // hand-typed URL with malformed params should not blow up to the error
  // boundary — fall back to the no-filter "All Policies" view instead.
  const rawSearch = useSearch({ strict: false });
  const parseResult = policyMrpSearchSchema.safeParse(rawSearch);
  const search = parseResult.success ? parseResult.data : {};
  const navigate = useNavigate();
  const agencyCode = search.agencyCode;
  const activePolicyBind = search.activePolicyBind === "true";

  // Per-tab persistence — leaving the table to drill into a policy and
  // hitting Back restores the same filters/sort the user left.
  const [filterText, setFilterText] = useState("");
  const debouncedFilter = useDebouncedValue(filterText, 200);
  // Inline column filters — multi-select status replaces the old standalone
  // status dropdown; the two dates pop a from/to range from their header.
  const [statusFilter, setStatusFilter] = useSessionState<string[]>(
    "c360:policy-mrp:statusFilter",
    [],
  );
  const [effectiveDateRange, setEffectiveDateRange] = useSessionState<{
    from?: string;
    to?: string;
  }>("c360:policy-mrp:effectiveDateRange", {});
  const [expirationDateRange, setExpirationDateRange] = useSessionState<{
    from?: string;
    to?: string;
  }>("c360:policy-mrp:expirationDateRange", {});
  const [sortKey, setSortKey] = useSessionState<SortKey>("c360:policy-mrp:sortKey", "policyNumber");
  const [sortDir, setSortDir] = useSessionState<SortDir>("c360:policy-mrp:sortDir", "asc");
  // Quick-filter chip presets. "Active only" is URL-driven (the API scopes
  // to status='Active' server-side); the other two are client-side derived
  // from already-loaded rows.
  const [renewing60Days, setRenewing60Days] = useSessionState<boolean>(
    "c360:policy-mrp:renewing60",
    false,
  );
  const [hasOpenClaims, setHasOpenClaims] = useSessionState<boolean>(
    "c360:policy-mrp:hasOpenClaims",
    false,
  );

  // Per-user column visibility + order. Policy Number is hardcoded sticky-
  // first (it's the row's identifier) so it isn't part of this set.
  const {
    orderedKeys: orderedSecondaryKeys,
    visibleKeys: visibleSecondaryKeys,
    isHidden: isSecondaryHidden,
    setHidden: setSecondaryHidden,
    setOrder: setSecondaryOrder,
    resetToDefaults: resetSecondaryColumns,
  } = useTablePreferences<SecondaryColumnKey>({
    tableId: "policy-mrp",
    defaultOrder: POLICY_MRP_DEFAULT_ORDER,
  });
  // Visible window grows in PAGE_SIZE steps via "Load more rows". Reset to
  // PAGE_SIZE whenever the filter set changes so the new result list opens
  // at the top.
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  function handleSort(key: SortKey) {
    // Dates default to descending (most recent first); names/strings to ascending.
    const next = nextSortState({ key: sortKey, dir: sortDir }, key, {
      effectiveDate: "desc",
      expirationDate: "desc",
    });
    setSortKey(next.key);
    setSortDir(next.dir);
  }

  const {
    data: policies,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: policyKeys.mrp({
      agencyCode,
      activePolicyBind: activePolicyBind ? "true" : undefined,
    }),
    queryFn: () => {
      const params = new URLSearchParams();
      if (agencyCode) params.set("agencyCode", agencyCode);
      if (activePolicyBind) params.set("activePolicyBind", "true");
      return apiGet(`/api/policies/mrp?${params.toString()}`, PolicyRowsSchema);
    },
  });

  // Distinct statuses for the status dropdown — drawn from the rows we have
  // so the user only sees options that exist in the current result set.
  const statusOptions = useMemo(() => {
    if (!policies) return [];
    const set = new Set<string>();
    for (const p of policies) {
      if (p.policyStatus) set.add(p.policyStatus);
    }
    return Array.from(set).sort();
  }, [policies]);

  // Refinement happens client-side over the already-fetched rows. Free-text
  // search matches policy number OR insured name; each column filter is
  // applied independently. Then sort by the active column. Pagination clamps
  // the visible window below.
  const filtered = useMemo(() => {
    if (!policies) return [];
    const term = debouncedFilter.trim().toLowerCase();
    const statusSet = new Set(statusFilter);
    const effFrom = effectiveDateRange.from;
    const effTo = effectiveDateRange.to;
    const expFrom = expirationDateRange.from;
    const expTo = expirationDateRange.to;
    // "Renewing in 60 days" cutoff: today ≤ expirationDate ≤ today + 60.
    // Computed once per memo evaluation so it stays consistent across the
    // filter pass.
    let renewFrom: string | undefined;
    let renewTo: string | undefined;
    if (renewing60Days) {
      const today = new Date();
      const future = new Date();
      future.setDate(future.getDate() + 60);
      // Use the user's LOCAL date — toISOString returns UTC, which silently
      // drops/adds a day for users west of UTC in the late evening (or east
      // in the early morning). Expiration dates compare lexicographically
      // against the same YYYY-MM-DD shape.
      renewFrom = toLocalIsoDate(today);
      renewTo = toLocalIsoDate(future);
    }
    const matched = policies.filter((p) => {
      if (statusSet.size > 0 && (!p.policyStatus || !statusSet.has(p.policyStatus))) return false;
      if (effFrom && (!p.effectiveDate || p.effectiveDate < effFrom)) return false;
      if (effTo && (!p.effectiveDate || p.effectiveDate > effTo)) return false;
      if (expFrom && (!p.expirationDate || p.expirationDate < expFrom)) return false;
      if (expTo && (!p.expirationDate || p.expirationDate > expTo)) return false;
      if (renewFrom && renewTo) {
        if (!p.expirationDate || p.expirationDate < renewFrom || p.expirationDate > renewTo)
          return false;
      }
      if (hasOpenClaims && p.openClaimCount <= 0) return false;
      if (!term) return true;
      const number = p.policyNumber.toLowerCase();
      const name = (p.insuredName ?? "").toLowerCase();
      return number.includes(term) || name.includes(term);
    });
    const sorted = [...matched];
    sorted.sort((a, b) => {
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      const cmp = String(av).localeCompare(String(bv));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [
    policies,
    debouncedFilter,
    statusFilter,
    effectiveDateRange.from,
    effectiveDateRange.to,
    expirationDateRange.from,
    expirationDateRange.to,
    renewing60Days,
    hasOpenClaims,
    sortKey,
    sortDir,
  ]);

  const visibleRows = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  const title = filterTitle(agencyCode, activePolicyBind);
  // Section-bar subtitle always shows the total filtered row count so the
  // user can see at a glance how many records the current view covers.
  const subtitle = policies
    ? filtered.length === policies.length
      ? `${policies.length.toLocaleString()} polic${policies.length === 1 ? "y" : "ies"}`
      : `${filtered.length.toLocaleString()} of ${policies.length.toLocaleString()} polic${policies.length === 1 ? "y" : "ies"}`
    : undefined;

  const hasActiveRefinement =
    debouncedFilter.trim().length > 0 ||
    statusFilter.length > 0 ||
    Boolean(effectiveDateRange.from) ||
    Boolean(effectiveDateRange.to) ||
    Boolean(expirationDateRange.from) ||
    Boolean(expirationDateRange.to) ||
    renewing60Days ||
    hasOpenClaims;

  function clearAllRefinements() {
    setFilterText("");
    setStatusFilter([]);
    setEffectiveDateRange({});
    setExpirationDateRange({});
    setRenewing60Days(false);
    setHasOpenClaims(false);
    setVisibleCount(PAGE_SIZE);
  }

  function toggleActivePolicyBind() {
    const next = { ...search };
    if (activePolicyBind) {
      delete next.activePolicyBind;
    } else {
      next.activePolicyBind = "true";
    }
    navigate({ to: "/policy/list", search: next });
    setVisibleCount(PAGE_SIZE);
  }

  const exportColumns: ExportColumn<PolicyRow>[] = [
    { key: "policyNumber", label: "Policy Number", value: (r) => r.policyNumber },
    { key: "insuredName", label: "Insured", value: (r) => r.insuredName },
    { key: "policyStatus", label: "Status", value: (r) => r.policyStatus },
    { key: "effectiveDate", label: "Effective", value: (r) => r.effectiveDate },
    { key: "expirationDate", label: "Expiration", value: (r) => r.expirationDate },
  ];
  const exportFilename = agencyCode ? `policies-agency-${agencyCode}` : "policies";

  const breadcrumbTrail: BreadcrumbTrailItem[] = agencyCode
    ? [
        {
          key: "agency",
          link: (
            <Link to="/agency/$agentNumber" params={{ agentNumber: agencyCode }}>
              Agency {agencyCode}
            </Link>
          ),
        },
      ]
    : [];

  return (
    <div className="space-y-3">
      <PageBreadcrumbs trail={breadcrumbTrail} current="Policies" />

      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-muted-foreground text-xs">Quick filters:</span>
        <QuickFilterChip
          label="Active only"
          description="Policies with status Active. Excludes cancelled, expired, and pending."
          active={activePolicyBind}
          onToggle={toggleActivePolicyBind}
        />
        <QuickFilterChip
          label="Renewing in 60 days"
          description="Policies with an expiration date in the next 60 days."
          active={renewing60Days}
          onToggle={() => {
            setRenewing60Days(!renewing60Days);
            setVisibleCount(PAGE_SIZE);
          }}
        />
        <QuickFilterChip
          label="Has open claims"
          description="Policies with at least one claim still open."
          active={hasOpenClaims}
          onToggle={() => {
            setHasOpenClaims(!hasOpenClaims);
            setVisibleCount(PAGE_SIZE);
          }}
        />
      </div>

      <SectionBar
        title={title}
        subtitle={subtitle}
        rightSlot={
          policies && policies.length > 0 ? (
            <div className="flex items-center gap-2">
              <div className="relative w-44 sm:w-56">
                <Search className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-2.5 size-3.5 text-primary-foreground/60" />
                <Input
                  value={filterText}
                  onChange={(e) => {
                    setFilterText(e.target.value);
                    setVisibleCount(PAGE_SIZE);
                  }}
                  placeholder="Search..."
                  className="h-7 border-primary-foreground/20 bg-primary-foreground/10 pl-8 text-primary-foreground text-xs placeholder:text-primary-foreground/60 focus-visible:ring-1 focus-visible:ring-primary-foreground/40"
                />
              </div>
              <ExportButton
                tone="primary"
                filename={exportFilename}
                columns={exportColumns}
                rows={filtered}
              />
              <ColumnManager<SecondaryColumnKey>
                columns={POLICY_MRP_COLUMN_DEFS}
                orderedKeys={orderedSecondaryKeys}
                isHidden={isSecondaryHidden}
                tone="primary"
                onReorder={setSecondaryOrder}
                onToggleHidden={setSecondaryHidden}
                onReset={resetSecondaryColumns}
              />
            </div>
          ) : null
        }
      />

      <div className="-mt-4 rounded-md rounded-t-none border border-t-0 bg-card">
        {isLoading && (
          <div className="space-y-2 p-3">
            {["sk-0", "sk-1", "sk-2", "sk-3", "sk-4"].map((k) => (
              <div key={k} className="h-10 animate-pulse rounded bg-muted/40" />
            ))}
          </div>
        )}

        {isError && (
          <div className="p-8 text-center">
            <p className="text-destructive">Failed to load policies.</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        )}

        {!isLoading && !isError && policies && policies.length === 0 && (
          <div className="p-8 text-center">
            <FileText className="mx-auto h-10 w-10 text-muted-foreground/40" />
            <p className="mt-3 text-muted-foreground">No policies match these filters.</p>
          </div>
        )}

        {!isLoading && !isError && policies && policies.length > 0 && filtered.length === 0 && (
          <div className="p-8 text-center">
            <FileText className="mx-auto h-10 w-10 text-muted-foreground/40" />
            <p className="mt-3 text-muted-foreground">No policies match the current refinement.</p>
            {hasActiveRefinement && (
              <Button variant="outline" size="sm" className="mt-3" onClick={clearAllRefinements}>
                Clear filters
              </Button>
            )}
          </div>
        )}

        {filtered.length > 0 && (
          <Table>
            <TableHeader className="bg-card">
              <TableRow className="hover:bg-transparent">
                <SortableHeader
                  label="Policy Number"
                  sortKey="policyNumber"
                  activeKey={sortKey}
                  activeDir={sortDir}
                  onSort={handleSort}
                  className="sticky left-0 z-20 bg-card"
                />
                {visibleSecondaryKeys.map((key) => {
                  switch (key) {
                    case "insuredName":
                      return (
                        <SortableHeader
                          key={key}
                          label="Insured Name"
                          sortKey="insuredName"
                          activeKey={sortKey}
                          activeDir={sortDir}
                          onSort={handleSort}
                        />
                      );
                    case "policyStatus":
                      return (
                        <SortableHeader
                          key={key}
                          label="Status"
                          sortKey="policyStatus"
                          activeKey={sortKey}
                          activeDir={sortDir}
                          onSort={handleSort}
                          filter={
                            <MultiSelectColumnFilter
                              ariaLabel="Filter by status"
                              options={statusOptions}
                              value={statusFilter}
                              onChange={(next) => {
                                setStatusFilter(next);
                                setVisibleCount(PAGE_SIZE);
                              }}
                            />
                          }
                        />
                      );
                    case "effectiveDate":
                      return (
                        <SortableHeader
                          key={key}
                          label="Effective"
                          sortKey="effectiveDate"
                          activeKey={sortKey}
                          activeDir={sortDir}
                          onSort={handleSort}
                          filter={
                            <DateRangeColumnFilter
                              ariaLabel="Filter by effective date range"
                              value={effectiveDateRange}
                              onChange={(next) => {
                                setEffectiveDateRange(next);
                                setVisibleCount(PAGE_SIZE);
                              }}
                            />
                          }
                        />
                      );
                    case "expirationDate":
                      return (
                        <SortableHeader
                          key={key}
                          label="Expiration"
                          sortKey="expirationDate"
                          activeKey={sortKey}
                          activeDir={sortDir}
                          onSort={handleSort}
                          filter={
                            <DateRangeColumnFilter
                              ariaLabel="Filter by expiration date range"
                              value={expirationDateRange}
                              onChange={(next) => {
                                setExpirationDateRange(next);
                                setVisibleCount(PAGE_SIZE);
                              }}
                            />
                          }
                        />
                      );
                    default:
                      return null;
                  }
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleRows.map((p) => {
                // Forward agency context so the Policy Dashboard breadcrumb
                // shows a clickable chain back to the agency the user drilled
                // in from.
                const fromHint = agencyCode ? encodeFromAgency(agencyCode) : undefined;
                const go = () =>
                  navigate({
                    to: "/policy/$policyNumber",
                    params: { policyNumber: p.policyNumber },
                    search: fromHint ? { from: fromHint } : {},
                  });
                return (
                  <TableRow
                    key={p.id}
                    className="cursor-pointer"
                    onClick={go}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        go();
                      }
                    }}
                    tabIndex={0}
                  >
                    <TableCell className="sticky left-0 z-10 bg-card font-medium text-primary">
                      <NumberHoverCard
                        preview={{
                          kind: "policy",
                          number: p.policyNumber,
                          insuredName: p.insuredName,
                          status: p.policyStatus,
                          effectiveDate: p.effectiveDate,
                          expirationDate: p.expirationDate,
                        }}
                        copyLabel={`policy ${p.policyNumber}`}
                      >
                        <span>{p.policyNumber}</span>
                      </NumberHoverCard>
                    </TableCell>
                    {visibleSecondaryKeys.map((key) => {
                      switch (key) {
                        case "insuredName":
                          return <TableCell key={key}>{p.insuredName ?? "—"}</TableCell>;
                        case "policyStatus":
                          return (
                            <TableCell key={key}>
                              {p.policyStatus ? (
                                <StatusBadge
                                  statusKey={policyStatusKey(p.policyStatus)}
                                  label={p.policyStatus}
                                />
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                          );
                        case "effectiveDate":
                          return <TableCell key={key}>{formatDate(p.effectiveDate)}</TableCell>;
                        case "expirationDate":
                          return <TableCell key={key}>{formatDate(p.expirationDate)}</TableCell>;
                        default:
                          return null;
                      }
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {hasMore && (
          <div className="flex items-center justify-between gap-3 border-t px-4 py-3 text-sm">
            <div className="text-muted-foreground">
              Showing {visibleRows.length.toLocaleString()} of {filtered.length.toLocaleString()}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setVisibleCount((current) => Math.min(filtered.length, current + PAGE_SIZE))
              }
            >
              Load 25 more
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
